import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createTransaction } from '../api/apis';
import { getFromLocalStorage } from '../utils/localStorage';
import { toast } from 'react-toastify';

export default function PaymentCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate processing in React StrictMode
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const handlePaymentCallback = async () => {
      try {
        console.log('🔵 ========== PAYMENT CALLBACK START ==========');
        const orderId = searchParams.get('order_id');
        const status = searchParams.get('status');
        const urlOrigin = searchParams.get('origin');
        
        console.log('💳 Payment callback received');
        console.log('📋 All URL params:', Object.fromEntries(searchParams));
        console.log('🆔 Order ID:', orderId);
        console.log('📊 Status:', status);

        // Get card data from sessionStorage (set before payment)
        console.log('🔍 Looking for pendingCardPurchase in sessionStorage...');
        const cardDataStr = sessionStorage.getItem('pendingCardPurchase');
        console.log('📝 Session storage value:', cardDataStr);
        
        if (!cardDataStr) {
          console.error('❌ No pendingCardPurchase found in sessionStorage!');
          throw new Error('No pending purchase found');
        }

        const cardData = JSON.parse(cardDataStr);
        console.log('📦 Card data parsed:', cardData);
        const purchaseOrigin = (urlOrigin || cardData?.purchaseOrigin || 'unknown').toLowerCase();
        const isStreamerOrigin = purchaseOrigin === 'streamer';
        console.log('🌐 Purchase origin:', purchaseOrigin);

        const token = getFromLocalStorage('token');
        const user = getFromLocalStorage('user');
        
        console.log('🔑 Token exists:', !!token);
        console.log('👤 User:', user);

        // Create transaction
        const transactionData = {
          cardId: cardData._id,
          streamerEmailId: cardData.streamerEmailId,
          userEmailId: user?.emailId || user?.email,
          title: cardData.cardTitle,
          description: cardData.description,
          ...(isStreamerOrigin ? { isUsed: true } : {}),
          purchaseSource: isStreamerOrigin ? 'streamer-page' : 'dashboard',
          paymentId: orderId,
          orderId: orderId
        };

        console.log('🔵 Creating transaction with data:', JSON.stringify(transactionData, null, 2));

        const response = await createTransaction(transactionData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'User-Info': user?.emailId || user?.username || user
          }
        });

        console.log('✅ Transaction response received:', JSON.stringify(response.data, null, 2));

        if (response.data.status === 'success') {
          console.log('🎉 Transaction created successfully!');
          sessionStorage.removeItem('pendingCardPurchase');
          
          // Show success toast
          toast.success('Card purchased successfully!');
          
          if (isStreamerOrigin) {
            // Get streamer username from card data
            const streamerUsername = cardData.streamerUsername || cardData.username;
            console.log('↩️ Redirecting to streamer page:', streamerUsername);
            setTimeout(() => {
              if (streamerUsername) {
                navigate(`/streamers/cards?username=${encodeURIComponent(streamerUsername)}`);
              } else {
                navigate('/streamers');
              }
            }, 1200);
          } else {
            // Dashboard purchase -> go to My Cards
            setTimeout(() => {
              navigate('/my-cards');
            }, 800);
          }
        } else {
          throw new Error('Transaction creation failed');
        }
      } catch (error) {
        console.error('❌ ========== PAYMENT CALLBACK ERROR ==========');
        console.error('Error:', error);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        
        toast.error('Payment processing failed. Please contact support.');
        
        setTimeout(() => {
          navigate('/streamers');
        }, 1500);
      } finally {
        setProcessing(false);
      }
    };

    handlePaymentCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
      <div className="text-center">
        {processing ? (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-400 mx-auto mb-4"></div>
            <p className="text-yellow-400 text-xl font-semibold">Processing your payment...</p>
          </>
        ) : (
          <p className="text-white text-xl">Redirecting...</p>
        )}
      </div>
    </div>
  );
}
